import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import { useSlideshowImagePreload } from 'ts/common/components/slideshows/slideshowImagePreloader';
import {
    OverlapTimelineLayout,
    buildOverlapTimeline,
    canAdvanceToTime,
    getBoundaryTransitionDurations,
    getSlideDurationMs,
    resolvePlaybackTime
} from 'ts/common/components/slideshows/slideshowOverlapTimeline';
import SlideshowPlayer, { ISlideshowPlayerProps } from './SlideshowPlayer';

/**
 * Number of leading slides to preload before playback is allowed to start. Buffering
 * just this many up front (rather than the whole slideshow) keeps the initial wait
 * short even for long slideshows, while still covering enough of the early
 * transitions that mid-playback stalls are rare on a typical connection.
 */
const INITIAL_BUFFER_SLIDE_COUNT = 5;

export type ISlideshowPlayerTickerProps = Omit<
    ISlideshowPlayerProps,
    'currentTime' | 'isBuffering' | 'loadingIndicator'
> & {
    /**
     * Controls playback. When false (default) the clock is paused at its current position.
     * Toggle to true to start/resume, false to pause.
     */
    isPlaying?: boolean;
    /** Called once when non-looping playback reaches the end. */
    onPlaybackEnd?: () => void;
    /**
     * Rendered centered over the visible slideshow content while buffering (initial
     * or mid-playback). Callers should pass a context-appropriate loading indicator
     * — e.g. Studio's `LoadingIcon` or Client's `LoadingIndicator`.
     */
    loadingIndicator: React.ReactNode;
};

/**
 * Imperative handle exposed via ref, for cases where the caller needs to control
 * the playback position directly (scrub bar, audio sync, keyboard seek).
 */
export interface ISlideshowPlayerTickerHandle {
    /** Jump playback to an arbitrary position without interrupting the RAF loop. */
    seek: (timeMs: number) => void;
}

/**
 * Always prefer this component over using SlideshowPlayer directly.
 *
 * SlideshowPlayerTicker owns the playback clock internally, driven by
 * requestAnimationFrame (RAF) — a browser API that fires just before each display
 * repaint (~60fps), syncs to the screen's refresh rate, and automatically pauses
 * when the tab is hidden. This means the rapid per-frame state updates stay
 * isolated inside this subtree: the surrounding UI (editor panels, settings, slide
 * thumbnails, etc.) does not re-render on every animation frame.
 *
 * For cases where the caller needs to control the playback position directly
 * (editor scrub bar, audio sync, seeking to a specific frame), obtain a ref to
 * this component and call `ref.current.seek(milliseconds)`. The ticker remains the
 * single owner of currentTime in all cases.
 *
 * Use the raw SlideshowPlayer only when you need a fully stateless renderer —
 * for example, rendering a frozen preview frame at a fixed time.
 */
const SlideshowPlayerTicker = forwardRef<ISlideshowPlayerTickerHandle, ISlideshowPlayerTickerProps>(
    (
        { isPlaying = false, slideshow, onTimeChange, onPlaybackEnd, loadingIndicator, ...rest },
        ref
    ) => {
        const [currentTime, setCurrentTime] = useState(0);
        const [isStalledMidPlayback, setIsStalledMidPlayback] = useState(false);
        const rafRef = useRef<number | null>(null);
        const lastTimestampRef = useRef<number | null>(null);
        const currentTimeRef = useRef(currentTime);
        const layoutRef = useRef<OverlapTimelineLayout>(
            buildOverlapTimeline([], 0, { loop: false })
        );

        currentTimeRef.current = currentTime;

        const layout = useMemo(
            () =>
                buildOverlapTimeline(
                    slideshow.slides.map(getSlideDurationMs),
                    getBoundaryTransitionDurations(slideshow),
                    { loop: slideshow.loopToFitMusic }
                ),
            [slideshow]
        );

        layoutRef.current = layout;

        const readyIndices = useSlideshowImagePreload(slideshow.slides);
        const readyIndicesRef = useRef<ReadonlySet<number>>(readyIndices);
        readyIndicesRef.current = readyIndices;

        // Gates playback start until this many leading slides have preloaded — see
        // INITIAL_BUFFER_SLIDE_COUNT. Naturally re-evaluates to false whenever
        // `slideshow.slides` changes, since the preload hook resets readyIndices then.
        const initialBufferTarget = Math.min(INITIAL_BUFFER_SLIDE_COUNT, slideshow.slides.length);
        const hasBufferedInitial = useMemo(() => {
            for (let i = 0; i < initialBufferTarget; i++) {
                if (!readyIndices.has(i)) {
                    return false;
                }
            }

            return true;
        }, [readyIndices, initialBufferTarget]);

        useImperativeHandle(ref, () => ({
            seek: (timeMs: number) => {
                // Reset the delta reference so the next RAF tick doesn't treat
                // the gap between the seek point and the previous frame as elapsed time.
                lastTimestampRef.current = null;
                setCurrentTime(resolvePlaybackTime(timeMs, layoutRef.current));
            }
        }));

        useEffect(() => {
            // Also holds off while the initial buffer isn't ready yet — re-runs (and
            // starts the loop) once hasBufferedInitial flips true, provided isPlaying
            // is still true.
            if (!isPlaying || !hasBufferedInitial) {
                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }

                lastTimestampRef.current = null;
                return;
            }

            const startLayout = layoutRef.current;

            // Only runs when isPlaying becomes true — restart from the beginning after
            // the end, without resetting when layout changes mid-playback.
            if (!startLayout.loop) {
                setCurrentTime((timeMs) => (timeMs >= startLayout.totalDuration ? 0 : timeMs));
            }

            let cancelled = false;
            // Reset so the first tick doesn't compute a delta from a stale timestamp
            // (mirrors the same guard in seek()). Fixes resume-from-wrong-position.
            lastTimestampRef.current = null;

            const tick = (timestamp: number) => {
                // If the effect was cleaned up before this RAF fired, bail out
                // without scheduling the next frame (prevents the RAF loop from
                // continuing to run after a pause).
                if (cancelled) {
                    return;
                }

                if (lastTimestampRef.current !== null) {
                    const deltaMs = timestamp - lastTimestampRef.current;
                    const tickLayout = layoutRef.current;
                    const timeMs = currentTimeRef.current;
                    const nextTimeMs = timeMs + deltaMs;
                    let reachedEnd = false;
                    let stalled = false;
                    let nextCurrentTime = timeMs;

                    if (!tickLayout.loop && nextTimeMs >= tickLayout.totalDuration) {
                        reachedEnd = true;
                        nextCurrentTime = tickLayout.totalDuration;
                    } else {
                        const resolvedNext = resolvePlaybackTime(nextTimeMs, tickLayout);

                        // Buffer: hold at the last position rather than starting a
                        // transition into a slide whose image hasn't finished preloading.
                        if (canAdvanceToTime(resolvedNext, tickLayout, readyIndicesRef.current)) {
                            nextCurrentTime = resolvedNext;
                        } else {
                            stalled = true;
                        }
                    }

                    setCurrentTime(nextCurrentTime);
                    setIsStalledMidPlayback(stalled);

                    if (reachedEnd) {
                        lastTimestampRef.current = null;
                        onPlaybackEnd?.();
                        return;
                    }
                }

                lastTimestampRef.current = timestamp;
                rafRef.current = requestAnimationFrame(tick);
            };

            rafRef.current = requestAnimationFrame(tick);

            return () => {
                cancelled = true;

                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }

                lastTimestampRef.current = null;
            };
        }, [isPlaying, hasBufferedInitial, onPlaybackEnd]);

        useEffect(() => {
            setCurrentTime((timeMs) => resolvePlaybackTime(timeMs, layout));
        }, [layout]);

        useEffect(() => {
            onTimeChange?.(currentTime);
        }, [currentTime, onTimeChange]);

        // Gated on isPlaying so pausing hides the overlay immediately, rather than
        // waiting on a separate effect to clear a stale isStalledMidPlayback flag —
        // that effect would run after render, letting the overlay flash briefly.
        const isBuffering = isPlaying && (!hasBufferedInitial || isStalledMidPlayback);

        return (
            <SlideshowPlayer
                slideshow={slideshow}
                currentTime={currentTime}
                {...rest}
                isBuffering={isBuffering}
                loadingIndicator={loadingIndicator}
            />
        );
    }
);

SlideshowPlayerTicker.displayName = 'SlideshowPlayerTicker';

export default SlideshowPlayerTicker;
